import { useState, FormEvent, useEffect } from 'react'
import { isEmail } from '../../utils/stringUtils'
import { PublicQuizAPI } from '../../apis/PublicQuizAPI'
import { qcls, qfonts } from './quizTheme'
import bg1 from '../../images/bg-1.png'

interface Props {
  slug?: string
  onStart?: (attemptId: string, isResuming: boolean, assessmentName: string, questionCap: number | null) => void
  /** Called instead of onStart when no slug is provided — user picks the quiz next */
  onFormSubmit?: (name: string, email: string, role: string) => void
  initialName?: string
  initialEmail?: string
}

export default function RegisterPage({ slug, onStart, onFormSubmit, initialName, initialEmail }: Props) {
  const [firstName, setFirstName] = useState(initialName?.split(' ')[0] ?? process.env.REACT_APP_QUIZ_PREFILL_NAME?.split(' ')[0] ?? '')
  const [lastName, setLastName] = useState(initialName?.split(' ').slice(1).join(' ') ?? process.env.REACT_APP_QUIZ_PREFILL_NAME?.split(' ').slice(1).join(' ') ?? '')
  const [email, setEmail] = useState(initialEmail ?? process.env.REACT_APP_QUIZ_PREFILL_EMAIL ?? '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false)
  const [assessmentName, setAssessmentName] = useState('Digital capability')
  const [heroText, setHeroText] = useState('')
  const [description, setDescription] = useState('')

  const [questionCap, setQuestionCap] = useState<number | null>(null)

  useEffect(() => {
    if (!slug) return
    PublicQuizAPI.fetchInfo(slug)
      .then((info) => {
        if (info.name) setAssessmentName(info.name)
        if (info.heroText) setHeroText(info.heroText)
        if (info.description) setDescription(info.description)
        setQuestionCap(info.questionCap ?? null)
      })
      .catch(() => {})
  }, [slug])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    if (!firstName.trim()) return setError('Please enter your first name.')
    if (!lastName.trim()) return setError('Please enter your last name.')
    if (!isEmail(email.trim())) return setError('Please enter a valid email address.')
    if (!agreedToPrivacy) return setError('Please agree to the Privacy Policy to continue.')

    const fullName = `${firstName.trim()} ${lastName.trim()}`

    // No slug — just collect form data and let parent route to quiz selection
    if (!slug) {
      onFormSubmit?.(fullName, email.trim().toLowerCase(), '')
      return
    }

    try {
      setLoading(true)
      const result = await PublicQuizAPI.start({
        name: fullName,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        role: undefined,
        assessmentSlug: slug,
      })
      onStart?.(result.attemptId, result.isResuming, assessmentName, questionCap)
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen md:h-screen md:overflow-hidden relative flex flex-col md:flex-row">
      {/* ── Full-bleed background image ── */}
      <img src={bg1} alt="" className="absolute left-0 top-0 md:top-1/2 md:-translate-y-1/2 w-full md:w-[60%]" />

      {/* ── Left: text overlay ── */}
      <div className="relative z-10 md:w-5/12 flex-shrink-0 flex flex-col justify-center items-center pt-16 md:pt-0 px-10 md:px-10">
        <div className="pb-10 px-4 md:px-0">
          <h1 className={`text-[32px] font-black text-black leading-[1.1] mb-6 ${qfonts.heading}`}>
            {(heroText || assessmentName) && <>{heroText || assessmentName} </>}
            <span className="text-black">assessment</span>
          </h1>
          {description && (
            <p className={`text-black/70 text-sm leading-relaxed max-w-[22rem] mb-12 ${qfonts.body}`}>
              {description.split('\n').map((line, i, arr) => (
                <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
              ))}
            </p>
          )}

          <div className="space-y-4">
            {[
              'Adaptive — questions tailored to your level',
              'Takes 10–15 minutes to complete',
              'Receive a personalised report on completion',
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 bg-[#61d6ad] border border-black rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className={`text-black/90 text-sm ${qfonts.supporting}`}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right: form panel ── */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-10 pt-0 pb-14 md:py-14">
        <div className="w-full max-w-md rounded-lg shadow-2xl p-10" style={{ backgroundColor: 'rgba(245, 245, 247, 0.55)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}>
          <div className="mb-10">
            <h2 className="text-[32px] font-black text-black mb-2 font-quiz-heading text-center">Get started</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                autoComplete="given-name"
                className={`${qcls.input} !py-2`}
              />
            </div>

            <div>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
                autoComplete="family-name"
                className={`${qcls.input} !py-2`}
              />
            </div>

            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                autoComplete="email"
                className={`${qcls.input} !py-2`}
              />
            </div>

            {error && (
              <div className={qcls.errorBox}>
                <svg className="w-[22px] h-[22px] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                {error}
              </div>
            )}

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedToPrivacy}
                onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                className="mt-0.5 w-4 h-4 flex-shrink-0 accent-brand-yellow cursor-pointer"
              />
              <span className="text-sm text-gray-700 leading-snug">
                I agree to the terms of{' '}
                <a
                  href="https://www.unsw.edu.au/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-black hover:text-gray-600"
                >
                  UNSW's Privacy Policy
                </a>
              </span>
            </label>

            <div className="pt-4 flex justify-center">
            <button
              type="submit"
              disabled={loading}
              className="w-3/4 py-2 border flex items-center justify-between px-4 disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-95 active:scale-[0.99] transition-all bg-brand-yellow"
              style={{ color: 'rgb(26, 30, 43)', borderColor: 'rgb(26, 30, 43)', fontFamily: 'RobotoMono, Arial, sans-serif', fontSize: 16, fontWeight: 400 }}
            >
              {loading ? (
                <>
                  <svg className={qcls.spinner} fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Starting…
                </>
              ) : (
                <>
                  {slug ? 'Begin assessment' : 'Choose assessment'}
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </>
              )}
            </button>
            </div>

            <p className="text-xs text-center text-gray-400">
              {/* Have an existing session? Enter the same email to resume. */}
            </p>
          </form>
        </div>
      </div>

    </div>
  )
}
